import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, X, Image as ImageIcon } from "lucide-react";

const MAX_PHOTOS = 5;
const MAX_SIZE_MB = 8;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

/**
 * Reusable photo picker with previews. Holds files in local state until parent
 * submits them. Use onChange(files) to forward the File[] to the parent.
 */
export default function PhotoUpload({
  value = [],
  onChange,
  max = MAX_PHOTOS,
  label = "Photos (optional)",
  helper = "Up to 5 photos · JPG, PNG or WEBP · max 8MB each",
  testId = "photo-upload",
}) {
  const inputRef = useRef(null);
  const [error, setError] = useState("");

  const handleFiles = (files) => {
    setError("");
    const incoming = Array.from(files);
    const valid = [];
    for (const f of incoming) {
      if (!ACCEPTED.includes(f.type)) {
        setError(`"${f.name}" is not a supported image type.`);
        continue;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        setError(`"${f.name}" is larger than ${MAX_SIZE_MB}MB.`);
        continue;
      }
      valid.push(f);
    }
    const merged = [...value, ...valid].slice(0, max);
    if ([...value, ...valid].length > max) {
      setError(`Maximum ${max} photos. Extra files were dropped.`);
    }
    onChange(merged);
  };

  const remove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-900">{label}</p>
        <span className="text-xs text-slate-500">
          {value.length}/{max}
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
        {value.map((file, idx) => {
          const url = URL.createObjectURL(file);
          return (
            <div
              key={`${file.name}-${idx}`}
              className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group"
              data-testid={`${testId}-thumb-${idx}`}
            >
              <img src={url} alt={file.name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(idx)}
                aria-label="Remove photo"
                className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`${testId}-remove-${idx}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-orange-400 hover:bg-orange-50/50 transition-colors flex flex-col items-center justify-center text-slate-500 hover:text-orange-600"
            data-testid={`${testId}-add-btn`}
          >
            <Upload className="w-5 h-5 mb-1" />
            <span className="text-xs font-medium">Add</span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED.join(",")}
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.length) handleFiles(e.target.files);
          e.target.value = "";
        }}
        data-testid={`${testId}-input`}
      />
      <p className="text-xs text-slate-500 flex items-center gap-1">
        <ImageIcon className="w-3 h-3" />
        {helper}
      </p>
      {error && (
        <p className="text-xs text-red-600" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}
